// ============================================
// components/LocationSelector.jsx
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiMapPin } from 'react-icons/fi';
import { getCountries, getStates, getCities } from '../services/locationService';
import '../styles/Modals.scss';

const LocationSelector = ({ type, countryId, stateId, currentValue, onSelect, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      let data = [];
      if (type === 'country') {
        data = await getCountries();
      } else if (type === 'state') {
        if (!countryId) {
          setOptions([]);
          setFilteredOptions([]);
          setLoading(false);
          return;
        }
        data = await getStates(countryId);
      } else if (type === 'city') {
        if (!countryId || !stateId) {
          setOptions([]);
          setFilteredOptions([]);
          setLoading(false);
          return;
        }
        data = await getCities(stateId);
      }

      setOptions(data || []);
      setFilteredOptions(data || []);
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      setOptions([]);
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  }, [type, countryId, stateId]);

  useEffect(() => {
    loadOptions();
  }, [type, countryId, stateId, loadOptions]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchQuery, options]);

  const handleSelect = (item) => {
    onSelect({
      [type]: item.name,
      [`${type}Id`]: item.id,
    });
  };

  const getTitle = () => {
    switch (type) {
      case 'country': return 'Select Country';
      case 'state': return 'Select State';
      case 'city': return 'Select City';
      default: return 'Select Location';
    }
  };

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose}>
        <motion.div
          className="modal-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>{getTitle()}</h3>
            <button className="close-button" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${type}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div className="options-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="empty-state">
                <FiMapPin size={48} />
                <p>No {type}s found</p>
              </div>
            ) : (
              filteredOptions.map((item) => (
                <button
                  key={item.id}
                  className={`option-item ${currentValue === item.name ? 'selected' : ''}`}
                  onClick={() => handleSelect(item)}
                >
                  <span>{item.name}</span>
                  {currentValue === item.name && (
                    <FiMapPin className="selected-icon" />
                  )}
                </button>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LocationSelector;